import { Box, Button, Grid, Typography } from '@mui/material';
import { intersectionBy, isEmpty, toString } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { AuthDataProvider } from 'src/components/authProvider';
import { DynamicTooltipChip } from 'src/components/dynamicTooltipChip/DynamicTooltipChip';
import { FilterDropdownWithSearch } from 'src/components/filterDropdownWithSearch/FilterDropdownWithSearch';
import { Footer } from 'src/components/footer/Footer';
import { Header } from 'src/components/header';
import {
  AssignLocationsModal,
  PreviewLocationGroupsModal,
  PublicationModal,
  SimpleDialog,
} from 'src/components/modal';
import { useNavigateBlockerContext } from 'src/components/navigateBlockerProvider';
import { NoResultAlert } from 'src/components/noResultAlert';
import { Progress } from 'src/components/progress';
import { Snackbar } from 'src/components/snackbar';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { ViewOnlyAlert } from 'src/components/viewOnlyAlert';
import { MAX_LOCATION_TAG } from 'src/constants';
import { PATHS } from 'src/constants/paths';
import { useParamQuery } from 'src/hooks';
import { validateTagName } from 'src/hooks/api/locationTags/helper';
import { useDeleteScheduleLocationExclusions } from 'src/hooks/api/schedules/scheduleLocation/useDeleteScheduleLocationExclusions';
import { AuthClassification } from 'src/hooks/auth/auth.interface';
import { useGetPageParamsString } from 'src/hooks/useGetPageParamsString';
import {
  ILocation,
  IViewLocationGroupList,
  PublicationTypes,
} from 'src/interfaces';
import { lang, useUserRole } from 'src/utils';

import { retainScheduleDaypartOverrides } from '../listLocationsPage/helpers/scheduleDaypartOverrides.helper';
import { convertToViewLocationList } from './DetailLocationPage.helper';
import {
  ChannelGridContainer,
  ChipSpacer,
  Container,
  Content,
  Divider,
  OOTagsContainer,
  Section,
  StoreLocation,
  StyledBorder,
  StyledGridHeader,
  StyledItem,
  SubHeader,
  SubHeaderText,
} from './DetailLocationPage.styled';
import { useLocationEditPage } from './hooks/DetailLocationPage.hook';
import { Channel } from './modules/Channel/Channel';
import { OwnerOperatorTagsSection } from './modules/OwnerOperatorTagsSection/OwnerOperatorTagsSection';

export const DetailLocationPage = () => {
  const { locationCode = '' } = useParams();
  const searchString = useGetPageParamsString();
  const navigate = useNavigate();
  const { userRole } = useContext(AuthDataProvider);
  const { isViewOnly, isOwnerOperator, getLocationsPath } = useUserRole();
  const { getParamsValue } = useParamQuery();
  const groupID = getParamsValue('groupID')[0];
  const preferUnassigned = !groupID || groupID === 'undefined';

  const {
    formMethods,
    channelsData,
    isLoadingLoaction,
    isLoadingChannels,
    locationOwnerTagIds,
    ownerOperatorTags,
    selectedLocationGroups,
    setSelectedLocationGroups,
    isSaving,
    setIsSaving,
    saveModal,
    publishModal,
    assignLocationGroupModal,
    confirmLocationGroupModal,
    previewLocationGroupsModal,
    locationTagChanged,
    updateLocations,
    addLocationTag,
    addLocationOwnerTag,
    createOwnerTag,
    onClickSave,
    navigateToLocationGroupEdit,
    publicationCloseModal,
    locationTagCount,
    location,
    isBannerEnabled,
    isMessagingEnabled,
    setIsBannerEnabled,
    setIsMessagingEnabled,
    setIsBannerChanged,
    setIsMessagingChanged,
    isBannerChanged,
    isMessagingChanged,
    isContainsOutdoorType,
  } = useLocationEditPage(locationCode);

  const deleteScheduleLocationExclusions =
    useDeleteScheduleLocationExclusions();

  const onSaveLocationGroups = (
    selectedLocationGroups: IViewLocationGroupList[],
  ) => {
    setSelectedLocationGroups(selectedLocationGroups);

    assignLocationGroupModal.onCloseModal();
    confirmLocationGroupModal.onOpenModal();
  };

  const onHandleSaved = async () => {
    try {
      setIsSaving(true);
      const payload = {
        location_code: selectedLocation?.location_code ?? '',
        location_group_id: selectedLocationGroups.map((item) => item.id),
        hq: location?.hq,
      } as ILocation;

      const filteredLocationGroups = selectedLocationGroups.filter(
        (locationGroup) =>
          !locationGroup.locations.some(
            (location) => location.location_code === locationCode,
          ),
      );

      // * shared functionality between DetailLocationPage.tsx, UnassignedLocationTab.tsx, AssignLocationGroupPage.hook.ts
      await retainScheduleDaypartOverrides(
        locationCode,
        filteredLocationGroups,
        location?.locationGroups,
      );

      // cleanup schedules for old location groups (no exclusions for defaults)
      const previousLocationGroups = intersectionBy(
        location?.locationGroups,
        filteredLocationGroups,
        'channel_name',
      ).flatMap((locationGroup) =>
        !locationGroup.is_default ? locationGroup.id : [],
      );
      if (previousLocationGroups.length) {
        const allLocationGroupsUpdated =
          previousLocationGroups.length === location?.channel_id?.length;
        await deleteScheduleLocationExclusions.mutateAsync({
          locationCode,
          ...(!allLocationGroupsUpdated && { previousLocationGroups }),
        });
      }

      await updateLocations.mutateAsync([payload]);
      Snackbar.success(lang.successfullyAddedLocation);
      confirmLocationGroupModal.onCloseModal();
      previewLocationGroupsModal.onOpenModal();
    } catch (error) {
      Snackbar.error(lang.failedToAddLocation);
    } finally {
      setIsSaving(false);
    }
  };

  const { setModalState, resetModalState } = useNavigateBlockerContext();
  const hasUnsavedChanges =
    locationTagChanged || isMessagingChanged || isBannerChanged;
  const [isLeaving, setIsLeaving] = useState(false);

  const { reset, proceed, state } = useBlocker(
    ({ nextLocation, currentLocation }) =>
      nextLocation.pathname !== currentLocation.pathname &&
      hasUnsavedChanges &&
      !isLeaving,
  );

  useEffect(() => {
    if (state === 'blocked') {
      const customProceed = () => {
        flushSync(() => {
          setIsLeaving(true);
        });

        reset();

        setTimeout(() => {
          navigate(
            {
              pathname: getLocationsPath({ preferUnassigned }),
              search: searchString,
            },
            { replace: true },
          );
        }, 0);
      };

      setModalState({
        isOpen: true,
        proceedNavigation: customProceed,
        resetNavigation: reset,
      });
    }

    return resetModalState;
  }, [
    proceed,
    reset,
    resetModalState,
    setModalState,
    state,
    navigate,
    getLocationsPath,
    preferUnassigned,
    searchString,
  ]);

  const selectedLocation = convertToViewLocationList(
    location ?? ({} as ILocation),
  );

  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}>
        <Header
          title={toString(formMethods.getValues().location?.hq?.name)}
          breadcrumbItems={[
            {
              label: lang.locationGroup,
              link: !isOwnerOperator
                ? PATHS.LOCATION_GROUPS
                : PATHS.OO_LOCATION_PAGE,
            },
            {
              label: toString(formMethods.getValues().location?.hq?.name),
            },
          ]}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isViewOnly && <ViewOnlyAlert />}
          {!isViewOnly && userRole !== AuthClassification.OWNER_OPERATOR && (
            <Button
              sx={{
                height: 40,
              }}
              variant="contained"
              onClick={() => {
                assignLocationGroupModal.onOpenModal();
              }}>
              {lang.assignToLocationGroup}
            </Button>
          )}
        </Box>
      </Box>

      <Content container spacing={6}>
        <StyledItem item sm={12} md={3}>
          <Typography variant="h4">{lang.storeDetails}</Typography>
          <Divider />
          <Section container spacing={3}>
            <StoreLocation item xs={6}>
              <SubHeader>{lang.nsn}</SubHeader>
              <SubHeaderText>
                {formMethods.getValues().location?.hq?.store_number}
              </SubHeaderText>
            </StoreLocation>
            <StoreLocation item xs={6}>
              <SubHeader>{lang.storeNumber}</SubHeader>
              <SubHeaderText>
                {formMethods.getValues().location?.hq?.store_number}
              </SubHeaderText>
            </StoreLocation>
            <StoreLocation item xs={6}>
              <SubHeader>{lang.storeAddress}</SubHeader>
              <SubHeaderText>
                {formMethods.getValues().location?.hq?.address_line_1}
              </SubHeaderText>
              <SubHeaderText>
                {formMethods.getValues().location?.hq?.address_line_2}
              </SubHeaderText>
            </StoreLocation>
            <StoreLocation item xs={6}>
              <SubHeader>{lang.storeCity}</SubHeader>
              <SubHeaderText>
                {formMethods.getValues().location?.hq?.city}
              </SubHeaderText>
            </StoreLocation>
            <StoreLocation item xs={6}>
              <SubHeader>{lang.storeRegion}</SubHeader>
              <SubHeaderText>
                {formMethods.getValues().location?.hq?.region}
              </SubHeaderText>
            </StoreLocation>
            <StoreLocation item xs={6}>
              <SubHeader>{lang.storeZip}</SubHeader>
              <SubHeaderText>
                {formMethods.getValues().location?.hq?.postal_code}
              </SubHeaderText>
            </StoreLocation>
            <StoreLocation item xs={12}>
              <SubHeader>{lang.storeTimeZone}</SubHeader>
              <SubHeaderText>
                {formMethods.getValues().location?.hq?.timezone}
              </SubHeaderText>
            </StoreLocation>
            <StoreLocation item xs={12}>
              <SubHeader>{lang.storeLocationCode}</SubHeader>
              <SubHeaderText>{locationCode}</SubHeaderText>
            </StoreLocation>
            <StoreLocation item xs={6}>
              <SubHeader>{lang.storeLocationZone}</SubHeader>
              <SubHeaderText>
                {formMethods.getValues().location?.hq?.zone}
              </SubHeaderText>
            </StoreLocation>
            <StoreLocation item xs={6}>
              <SubHeader>{lang.storeLocationSubZone}</SubHeader>
              <SubHeaderText>
                {formMethods.getValues().location?.hq?.subzone}
              </SubHeaderText>
            </StoreLocation>
            <StoreLocation item xs={6}>
              <SubHeader>{lang.country}</SubHeader>
              <SubHeaderText>
                {formMethods.getValues().location?.hq?.country}
              </SubHeaderText>
            </StoreLocation>
            {!isLoadingLoaction &&
              formMethods.getValues().location &&
              (userRole !== AuthClassification.OWNER_OPERATOR ? (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h4">{lang.locationTagging}</Typography>
                    <Divider $marginBottom={15} />

                    {!isViewOnly ? (
                      <CustomTooltip
                        width={100}
                        placement="top-end"
                        title={lang.maxTags}
                        isHidden={locationTagCount < MAX_LOCATION_TAG}
                        skipValidateOverflow={true}>
                        <FilterDropdownWithSearch
                          name={lang.addTag}
                          placeholder={lang.addTag}
                          multiline={true}
                          onChange={(value: any) => {
                            addLocationTag(value);
                          }}
                          value={
                            formMethods?.getValues()?.location?.location_tag_id
                          }
                          field={`${locationTagCount}/${MAX_LOCATION_TAG}`} //displayCount
                          badgeOptions={{
                            align: 'bottom',
                          }}
                          disabled={locationTagCount >= MAX_LOCATION_TAG}
                        />
                      </CustomTooltip>
                    ) : (
                      // ! displayed info is different for isViewOnly (no displayCount)
                      // ! should be disabled (along with badges) for isViewOnly
                      <FilterDropdownWithSearch
                        name={lang.addTag}
                        placeholder={lang.addTag}
                        multiline={true}
                        onChange={(value: any) => {
                          addLocationTag(value);
                        }}
                        value={
                          formMethods?.getValues()?.location?.location_tag_id
                        }
                        badgeOptions={{
                          align: 'bottom',
                        }}
                        disabled
                      />
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h4">
                      {lang.ownerOperator.ownerOperatorTags}
                    </Typography>
                    <Divider $marginBottom={15} />
                    <OOTagsContainer>
                      {formMethods
                        .getValues()
                        .location?.locationOwnerTags.map((item, index) => (
                          <ChipSpacer key={index}>
                            <DynamicTooltipChip
                              chipName={item.name}
                              color="success"
                            />
                          </ChipSpacer>
                        ))}
                    </OOTagsContainer>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h4">{lang.locationTagging}</Typography>
                    <FilterDropdownWithSearch
                      name={lang.ownerOperator.ownerOperatorTags}
                      placeholder={lang.addTag}
                      multiline={true}
                      onChange={(value: any) => {
                        addLocationOwnerTag(value);
                      }}
                      onNotExistedEntry={createOwnerTag}
                      inputValidator={validateTagName}
                      value={locationOwnerTagIds}
                      selectOptions={ownerOperatorTags.map((tag) => ({
                        value: tag.id,
                        label: tag.name,
                      }))}
                      badgeOptions={{
                        align: 'bottom',
                        color: 'success',
                      }}
                    />
                  </Grid>
                  {isContainsOutdoorType && (
                    <OwnerOperatorTagsSection
                      isBannerEnabled={isBannerEnabled}
                      isMessagingEnabled={isMessagingEnabled}
                      setIsBannerEnabled={setIsBannerEnabled}
                      setIsMessagingEnabled={setIsMessagingEnabled}
                      setIsBannerChanged={setIsBannerChanged}
                      setIsMessagingChanged={setIsMessagingChanged}
                      isBannerChanged={isBannerChanged}
                      isMessagingChanged={isMessagingChanged}
                    />
                  )}
                </>
              ))}
          </Section>
        </StyledItem>
        <ChannelGridContainer>
          <StyledBorder>
            <StyledGridHeader>
              <Typography variant="h4">{lang.channels}</Typography>
            </StyledGridHeader>

            {isLoadingChannels || isLoadingLoaction ? (
              <Progress />
            ) : channelsData.length === 0 ? (
              <NoResultAlert />
            ) : (
              channelsData.map((item, index) => (
                <Channel
                  key={index}
                  channelName={item.channel_name}
                  locationGroup={item.locationGroup}
                  navigateToLocationGroupEdit={navigateToLocationGroupEdit}
                  layoutType={item.layoutType}
                  screenConfigurationLabel={item.screenConfigurationLabel}
                />
              ))
            )}
          </StyledBorder>
        </ChannelGridContainer>
        {assignLocationGroupModal.open && !isEmpty(selectedLocation) && (
          <AssignLocationsModal
            open={assignLocationGroupModal.open}
            onClose={assignLocationGroupModal.onCloseModal}
            onClickConfirm={onSaveLocationGroups}
            locationCode={location?.location_code}
          />
        )}
        {confirmLocationGroupModal.open && (
          <SimpleDialog
            open={confirmLocationGroupModal.open}
            cancelText={lang.cancel}
            confirmText={lang.yesConfirm}
            title={lang.confirmLocationGroupModal.title}
            subTitle={lang.confirmLocationGroupModal.subTitle}
            onClose={confirmLocationGroupModal.onCloseModal}
            onClickCancel={confirmLocationGroupModal.onCloseModal}
            isLoading={isSaving}
            disabled={isSaving}
            modalSize="sm"
            onClickConfirm={onHandleSaved}
          />
        )}

        {previewLocationGroupsModal.open && (
          <PreviewLocationGroupsModal
            open={previewLocationGroupsModal.open}
            onClose={previewLocationGroupsModal.onCloseModal}
            title={lang.previewLocationGroupsModal.title}
            locationName={selectedLocation?.name ?? ''}
            selectedLocationGroups={selectedLocationGroups}
          />
        )}
        <SimpleDialog
          open={saveModal.open}
          cancelText={lang.cancel}
          confirmText={lang.confirm}
          title={lang.titleTextSave}
          subTitle={lang.subTitlePublishConfirm}
          onClose={saveModal.onCloseModal}
          onClickCancel={saveModal.onCloseModal}
          modalSize="sm"
          onClickConfirm={onClickSave}
        />
        {publishModal.open && (
          <PublicationModal
            open={publishModal.open}
            locationCodes={[locationCode]}
            publishMessage={lang.successfullyPublished}
            publicationType={
              isOwnerOperator
                ? PublicationTypes.OVERRIDES
                : PublicationTypes.FULL
            }
            onClose={publicationCloseModal}
          />
        )}
      </Content>
      {!isViewOnly && (
        <Footer
          showFooterActions
          hasSubNav
          onCancel={() =>
            navigate({
              pathname: getLocationsPath({
                preferUnassigned,
              }),
              search: searchString,
            })
          }
          onSave={saveModal.onOpenModal}
          cancelLabel={lang.back}
          disableSave={
            !locationTagChanged && !isBannerChanged && !isMessagingChanged
          }
        />
      )}
    </Container>
  );
};
